import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { differenceInDays, subMonths, intervalToDuration } from 'date-fns';
import { User } from '@/types/server/user';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`;
}

export function formatCurrency(amount: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount / 100);
}

export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(value / 100);
}

export function formatSecondsToHuman(seconds: number) {
  if (seconds === 0) return '0s';

  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

  const parts = [];
  if (duration.hours && duration.hours > 0) {
    parts.push(`${duration.hours}h`);
  }
  if (duration.minutes && duration.minutes > 0) {
    parts.push(`${duration.minutes}m`);
  }
  if (duration.seconds && duration.seconds > 0) {
    parts.push(`${duration.seconds}s`);
  }

  if (parts.length === 0) {
    return '0s';
  }

  return parts.join(' ');
}

export function getBaseUrl() {
  if (typeof window !== 'undefined') return '';
  const vc = process.env.VERCEL_URL;
  if (vc) return `https://${vc}`;
  return 'http://localhost:4000';
}

export function generateStartDate() {
  return subMonths(new Date(), 3);
}

export function formatYAxis(value: number) {
  return (value / 100).toFixed(0);
}

export function getUserSubscriptionStatus(user: User): string {
  const isUserOnTrial =
    user.is_trial_active &&
    user.trial_start &&
    differenceInDays(new Date(), new Date(user.trial_start)) < 6;

  const mostRecentAndActiveSubscription = user.subscriptions?.find(
    (subscription) => subscription.status === 'ACTIVE'
  );

  const postTrialButDidntCovert =
    user.trial_start && !mostRecentAndActiveSubscription;

  const preTrial =
    user.company?.can_have_free_trials &&
    !user.trial_start &&
    !mostRecentAndActiveSubscription;

  if (isUserOnTrial) {
    return 'Free Trial';
  }
  if (postTrialButDidntCovert) {
    return 'Post Trial';
  }
  if (preTrial) {
    return 'Pre Trial';
  }
  return mostRecentAndActiveSubscription?.sub_product?.name || '';
}
